import React, { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

const GENDER_LABEL = { male: 'זכר', female: 'נקבה' };
const FUND_LABEL   = { comprehensive: 'מקיפה (4.38%)', supplementary: 'משלימה (4.00%)' };

function fmtBirthYM(year, month) {
  if (!year || !month) return '—';
  return `${year}/${String(month).padStart(2, '0')}`;
}

function ScenarioTable({ r }) {
  const inp = r.inputs || {};
  const ep     = r.externalParams || {};
  const hasExt  = r.externalParams != null;

  // Spouse external display
  const spouseExternal = ep.spouse_birth_year
    ? `${fmtBirthYM(ep.spouse_birth_year, ep.spouse_birth_month)} | שיעור: ${((ep.spouse_pension_share || 0) * 100).toFixed(0)}%`
    : '—';

  // Orphans our-side
  const orphansOurs = inp.orphans && inp.orphans.length > 0
    ? inp.orphans.map((o, j) => `ילד ${j + 1}: ${o.birthdate} (${o.orphanRate}%)`).join(' | ')
    : 'אין';

  // Orphans external
  let orphansExt = 'אין';
  if (ep.number_ofChilds) {
    const kids = [];
    for (let i = 1; i <= ep.number_ofChilds; i++) {
      const by = ep[`child${i}_birth_year`];
      const bm = ep[`child${i}_birth_month`];
      const ps = ep[`child${i}_pension_share`];
      kids.push(`ילד ${i}: ${fmtBirthYM(by, bm)} (${((ps || 0) * 100).toFixed(0)}%)`);
    }
    orphansExt = kids.join(' | ');
  }

  const rows = [
    { label: 'סוג קרן',         ours: FUND_LABEL[inp.fundType] || inp.fundType || '—',         ext: ep.fundType || '—' },
    { label: 'תאריך פרישה',     ours: inp.retirementDate || '—',              ext: ep.retirement_month ? `${ep.retirement_month}/${ep.retirement_year}` : '—' },
    { label: 'תאריך לידה',      ours: inp.memberBirthDate || '—',             ext: fmtBirthYM(ep.birth_year, ep.birth_month) },
    { label: 'מגדר',            ours: GENDER_LABEL[inp.memberGender] || inp.memberGender || '—', ext: GENDER_LABEL[ep.gender] || ep.gender || '—' },
    { label: 'תקופת הבטחה',    ours: inp.securityPeriod != null ? `${inp.securityPeriod} חודשים` : '—', ext: ep.minimum_years_of_payments != null ? `${ep.minimum_years_of_payments} שנים` : '—' },
    { label: 'דמי ניהול',       ours: inp.managementFee || '—',               ext: ep.management_fee != null ? `${ep.management_fee}` : '—' },
    { label: 'בן/בת זוג',       ours: inp.hasSpouse ? `יש | ${inp.spouseBirthDate || ''} (${inp.spousePensionRate}%)` : 'אין', ext: spouseExternal },
    { label: 'מגדר בן/בת זוג', ours: inp.hasSpouse ? (GENDER_LABEL[inp.spouseGender] || inp.spouseGender || '—') : '—', ext: inp.hasSpouse ? 'לא נשלח — מין הפוך אוטומטי' : '—' },
    { label: 'יתומים',          ours: orphansOurs,                             ext: orphansExt },
  ];

  const tdBase = { padding: '4px 6px', border: '1px solid #ddd', fontSize: '15px', verticalAlign: 'top' };
  const thBase = { ...tdBase, background: '#f0f0f0', fontWeight: 'bold', textAlign: 'right' };
  const coeffBg = { background: '#f8f8f8' };
  const matchBg = r.match ? '#e8f5e9' : '#ffebee';
  const matchColor = r.match ? '#2a7a40' : '#c00';

  return (
    <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: 'auto', marginTop: '8px', fontSize: '15px' }}>
      <thead>
        <tr>
          <th style={{ ...thBase, width: '160px' }}>שדה</th>
          <th style={{ ...thBase, width: '220px' }}>המערכת שלנו</th>
          <th style={{ ...thBase, width: '220px' }}>API חיצוני</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr key={row.label}>
            <td style={{ ...tdBase, fontWeight: '600', background: '#fafafa' }}>{row.label}</td>
            <td style={tdBase}>{row.ours}</td>
            <td style={tdBase}>{hasExt ? row.ext : <span style={{color:'#c00'}}>שגיאה</span>}</td>
          </tr>
        ))}

        {/* Coefficient row */}
        <tr>
          <td style={{ ...tdBase, ...coeffBg, fontWeight: 'bold', fontSize: '15px' }}>מקדם</td>
          <td style={{ ...tdBase, ...coeffBg, fontWeight: 'bold', fontSize: '15px', color: '#1a5bbf' }}>
            {r.ourCoefficient?.toFixed(6)}
          </td>
          <td style={{ ...tdBase, ...coeffBg, fontWeight: 'bold', fontSize: '15px', color: r.error ? '#c00' : '#1a7a3a' }}>
            {!hasExt ? <span style={{color:'#c00'}}>שגיאה</span> : r.error ? 'שגיאה' : r.externalCoefficient?.toFixed(6)}
          </td>
        </tr>

        {/* Diff row */}
        <tr>
          <td style={{ ...tdBase, background: matchBg, fontWeight: '600' }}>הפרש</td>
          <td colSpan={2} style={{ ...tdBase, background: matchBg, textAlign: 'center', color: matchColor, fontWeight: '600' }}>
            {r.diff !== null
              ? `${r.diff.toFixed(6)}  (${r.diffPct})`
              : r.error ? r.error : '—'}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

export default function CompareApp() {
  const [results, setResults]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/compare`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => { setResults(data); setLoading(false); })
      .catch(e  => { setFetchError(e.message); setLoading(false); });
  }, []);

  const page = { fontFamily: 'inherit', direction: 'rtl', padding: '24px', background: '#f5f5f5', minHeight: '100vh' };

  if (loading)    return <div style={page}><div style={{ fontSize: '20px', fontWeight: 'bold' }}>טוען נתונים…</div></div>;
  if (fetchError) return <div style={page}><div style={{ color: '#c00' }}>שגיאה בטעינה: {fetchError}</div></div>;

  const tested   = results.filter(r => !r.skipped).length;
  const matched  = results.filter(r => !r.skipped && r.match).length;
  const differed = results.filter(r => !r.skipped && !r.match).length;
  const skipped  = results.filter(r => r.skipped).length;
  const capOnly  = results.filter(r => r.capOnly).length;

  return (
    <div style={page}>
      <div style={{ fontSize: '22px', fontWeight: 'bold', marginBottom: '2px' }}>
        השוואת מקדמים — המערכת שלנו מול API חיצוני
      </div>
      <div style={{ fontSize: '13px', color: '#666', marginBottom: '28px' }}>pension-calculation.co.il</div>

      {results.map((r, i) => (
        <div key={i}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '16px', marginBottom: '6px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 'bold', fontSize: '16px' }}>{r.name}</span>
            {!r.skipped && r.coeffMonthly != null && (
              <span style={{ fontSize: '13px', color: '#555' }}>
                מודל משופר (Phoenix):&nbsp;
                <strong style={{ color: r.activeModel === 'monthly' ? '#1a5bbf' : '#333' }}>
                  {r.coeffMonthly?.toFixed(3)}
                </strong>
                &nbsp;&nbsp;|&nbsp;&nbsp;
                מודל פשוט (Simple):&nbsp;
                <strong style={{ color: r.activeModel === 'simple' ? '#1a5bbf' : '#333' }}>
                  {r.coeffSimple?.toFixed(3)}
                </strong>
              </span>
            )}
          </div>

          {r.skipped ? (
            <div style={{ color: '#888', fontStyle: 'italic', marginBottom: '8px' }}>
              ⏭ דולג: {r.skipReason}
            </div>
          ) : r.capOnly ? (
            <>
              <div style={{ background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '6px', padding: '8px 14px', marginBottom: '8px', fontSize: '13px', color: '#1565c0' }}>
                ℹ️ {r.capLimitation}
              </div>
              <ScenarioTable r={r} />
              {r.analysis && r.externalCoefficient !== null && (
                <div style={{ marginTop: '10px', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '6px', padding: '10px 14px' }}>
                  <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '6px', color: '#b26a00' }}>
                    ⚠️ ניתוח הפרש ({r.diff?.toFixed(4)}, {r.diffPct}):
                  </div>
                  {r.analysis.map((line, idx) => (
                    <div key={idx} style={{ fontSize: '13px', color: '#5c3d00', marginBottom: '3px' }}>• {line}</div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <ScenarioTable r={r} />
              {r.analysis && r.externalCoefficient !== null && (
                <div style={{ marginTop: '10px', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '6px', padding: '10px 14px' }}>
                  <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '6px', color: '#b26a00' }}>
                    ⚠️ ניתוח הפרש ({r.diff?.toFixed(4)}, {r.diffPct}):
                  </div>
                  {r.analysis.map((line, idx) => (
                    <div key={idx} style={{ fontSize: '13px', color: '#5c3d00', marginBottom: '3px' }}>
                      • {line}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {i < results.length - 1 && <hr style={{ margin: '20px 0', borderColor: '#ddd' }} />}
        </div>
      ))}

      <div style={{ background: '#fff', borderRadius: '8px', padding: '14px 20px', marginTop: '28px', boxShadow: '0 1px 4px rgba(0,0,0,0.1)', fontSize: '15px' }}>
        נבדקו: <strong>{tested}</strong> &nbsp;|&nbsp;
        תואמים ✅: <strong style={{ color: '#2a7a40' }}>{matched}</strong> &nbsp;|&nbsp;
        שונים ❌: <strong style={{ color: '#c00' }}>{differed}</strong> &nbsp;|&nbsp;
        היוון בלבד ℹ️: <strong style={{ color: '#1565c0' }}>{capOnly}</strong> &nbsp;|&nbsp;
        דולגו: <strong style={{ color: '#888' }}>{skipped}</strong>
      </div>
    </div>
  );
}
